import { Target, Zap, Clock } from "lucide-react";

const About = () => {
  const pillars = [
    {
      icon: Target,
      title: "Story",
      description: "Jedes Projekt braucht eine Geschichte, die hängen bleibt. Ich denke Content vom Ende her – was soll das Publikum fühlen?",
    },
    {
      icon: Zap,
      title: "Qualität",
      description: "Cinema-Look trifft auf Social-First-Denken. Technische Exzellenz ohne Kompromisse, aber immer mit Blick auf Plattform und Performance.",
    },
    {
      icon: Clock,
      title: "Geschwindigkeit",
      description: "KI-gestützte Workflows mit Freycreate ermöglichen schnelle Iterationen und Variantenvielfalt – ohne Qualitätsverlust.",
    },
  ];

  const timeline = [
    { year: "2020", event: "Start als Freelance Filmmaker – erste Projekte für lokale Marken, eigene Kurzfilme und kreative Reels" },
    { year: "2022", event: "Produktion der ersten Social-Media-Kampagnen, Imagefilme, Hochzeiten und Musikvideos – Fokus auf Emotion und visuelle Storys" },
    { year: "2023", event: "Zusammenarbeit mit zahlreichen Automobilkunden und Unternehmen – Aufbau von Social-Media-Content und Recruiting-Kampagnen" },
    { year: "2024", event: "Aufbau eines Kundenstamms und Kooperation mit Kreativen aus der Region – erste Aufträge für Städte und öffentliche Kampagnen" },
    { year: "2025", event: "Umsetzung internationaler Großprojekte (Dubai: 15-20 Reels) und Stadt-Kampagnen – Gründung der eigenen KI-Plattform freycreate.ai" },
  ];

  return (
    <section className="py-24 bg-background" data-testid="about-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            {/* Portrait */}
            <div className="order-2 lg:order-1">
              <div className="aspect-[4/5] bg-gradient-to-br from-slate-800 via-purple-900/20 to-slate-900 rounded-2xl overflow-hidden hover-tilt" data-testid="about-portrait">
                <img
                  src="https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/images/0uxu2fq0-kenny-profilbild.jpg"
                  alt="Kenny Frey - Filmemacher & Producer"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Text Content */}
            <div className="order-1 lg:order-2">
              <h2 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="about-title">
                Über mich
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Ich heiße Kenny Frey und komme aus Villingen-Schwenningen im Schwarzwald. Ich bin seit einigen Jahren selbstständiger leidenschaftlicher Videograf, habe mit eigenen Kurzfilmen begonnen und spezialisiere mich mittlerweile auf Werbe-, Hochzeits- und Musikvideos.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Ich arbeite mit vielen Agenturen rund um den Schwarzwald zusammen, um sie bei ihren Videoproduktionen zu unterstützen. Gleichzeitig betreue ich auch meine eigenen Kunden und helfe ihnen, ihre Ziele zu erreichen. Seit 2025 bin ich tief im Bereich KI für generatives Erstellen von Fotos und Videos drinnen – hier habe ich mein eigenes Tool entwickelt: <span className="text-[hsl(var(--accent))] font-semibold">Freycreate.ai</span>.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Mit über <span className="text-[hsl(var(--accent))] font-semibold">200+ Social Media Videos</span> für verschiedene Kunden bringe ich umfassende Erfahrung in der Content-Produktion mit – von Luxus-Automotive bis zu authentischen Produktvideos.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                <span className="text-[hsl(var(--accent))] font-semibold">Meine Leidenschaft:</span> Ich nutze Kameras wie meine rechte Hand – ich kann das im Schlaf. Nichts bringt mich mehr in einen <span className="text-[hsl(var(--accent))] font-semibold">Tunnelblick-Flow-Modus</span> wie eine aktive Produktion. Der Moment, wenn es nur mich, die Kamera und die aktuelle Szene gibt – da blende ich die ganze Welt und alle Probleme aus und bin zu 100% im Moment.
              </p>
            </div>
          </div>

          {/* 3 Pillars */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold mb-8 text-center">Arbeitsweise</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {pillars.map((pillar, index) => {
                const Icon = pillar.icon;
                return (
                  <div
                    key={index}
                    className="bg-secondary/30 rounded-2xl p-8 hover-tilt"
                    data-testid={`pillar-${index}`}
                  >
                    <div className="w-12 h-12 mb-4 rounded-xl bg-[hsl(var(--accent))]/10 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-[hsl(var(--accent))]" />
                    </div>
                    <h4 className="text-xl font-bold mb-3">{pillar.title}</h4>
                    <p className="text-muted-foreground leading-relaxed">
                      {pillar.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Partner Agenturen */}
          <div className="bg-secondary/30 rounded-2xl p-8 md:p-12">
            <h3 className="text-2xl font-bold mb-4 text-center">Netzwerk & Partner-Agenturen</h3>
            <p className="text-muted-foreground text-center mb-3 max-w-2xl mx-auto">
              <span className="text-white font-semibold">Ich arbeite nicht alleine.</span> Für größere
              Produktionen oder spezialisierte Disziplinen — Color Grading, Sound Design, 2nd Cam,
              Drohnenpilot, Producer, Set-Designer — hole ich gezielt Partner aus meinem Netzwerk
              dazu. Du bekommst Solo-Producer-Effizienz mit Agentur-Tiefe, wenn dein Projekt es
              verlangt.
            </p>
            <p className="text-xs text-muted-foreground text-center mb-8 max-w-2xl mx-auto opacity-80">
              Bewährtes Netzwerk im Schwarzwald & Süddeutschland — alle haben mit mir schon Sets geteilt.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
              <a
                href="https://ivorytower.productions"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center p-6 bg-white/5 rounded-xl hover:bg-white/10 transition-all hover:scale-105"
                title="Ivory Tower - Filmmanufaktur"
              >
                <img
                  src="https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/images/u89tlk2m-logo-white.svg"
                  alt="Ivory Tower Logo"
                  className="w-full h-auto max-w-[120px] opacity-80 hover:opacity-100 transition-opacity"
                />
              </a>
              <a
                href="https://www.zumblum.de"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center p-6 bg-white/5 rounded-xl hover:bg-white/10 transition-all hover:scale-105"
                title="ZUMBLUM - Kreativ-Küche"
              >
                <img
                  src="https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/images/r5333ibm-220118-zumblum-logo-rgb.webp"
                  alt="ZUMBLUM Logo"
                  className="w-full h-auto max-w-[120px] brightness-0 invert opacity-80 hover:opacity-100 transition-opacity"
                />
              </a>
              <a
                href="https://eofilm.de"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center p-6 bg-white/5 rounded-xl hover:bg-white/10 transition-all hover:scale-105"
                title="EO Film - Visual Engineering"
              >
                <img
                  src="https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/images/d3nu7r3k-logo-negative.png"
                  alt="EO Film Logo"
                  className="w-full h-auto max-w-[120px] opacity-80 hover:opacity-100 transition-opacity"
                />
              </a>
              <a
                href="https://schulzstudio.de"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center p-6 bg-white/5 rounded-xl hover:bg-white/10 transition-all hover:scale-105"
                title="schulzstudio - Alessio Schulz"
              >
                <img
                  src="https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/images/kpgg19a7-2cxzwc91-schulzstudio.svg"
                  alt="schulzstudio Logo"
                  className="w-full h-auto max-w-[120px] opacity-80 hover:opacity-100 transition-opacity"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
